import React, { useState, useEffect, useCallback } from 'react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const AdminAlumni = () => {
  const { user } = useAuth();
  const [alumni, setAlumni] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAlumni, setEditingAlumni] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    department_id: '',
    graduation_year: '',
    employment_status: '',
    employer: '',
    journey_description: '',
    email: '',
    phone: '',
    display_order: 0,
    photo: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  const canManage = user?.role === 'admin' || user?.role === 'editor';
  const roles = ['Chairman', 'Vice Chairman', 'Treasurer', 'Secretary', 'Member', 'Patron', 'Advisor'];

  const fetchAlumni = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/alumni');
      console.log('Fetched alumni:', response.data);
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching alumni:', error);
      toast.error('Failed to fetch alumni');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    fetchAlumni();
    fetchDepartments();
  }, [fetchAlumni]);

  // Refresh when refreshKey changes
  useEffect(() => {
    fetchAlumni();
  }, [refreshKey, fetchAlumni]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error('File too large! Maximum size is 5MB');
        return;
      }
      setFormData({ ...formData, photo: file });
      const previewUrl = URL.createObjectURL(file);
      setPhotoPreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.role) {
      toast.error('Name and role are required');
      return;
    }
    
    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('role', formData.role);
    submitData.append('department_id', formData.department_id);
    submitData.append('graduation_year', formData.graduation_year);
    submitData.append('employment_status', formData.employment_status);
    submitData.append('employer', formData.employer);
    submitData.append('journey_description', formData.journey_description);
    submitData.append('email', formData.email);
    submitData.append('phone', formData.phone);
    submitData.append('display_order', formData.display_order);
    if (formData.photo) {
      submitData.append('photo', formData.photo);
    }
    
    setLoading(true);
    try {
      if (editingAlumni) {
        await api.put(`/alumni/${editingAlumni.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Alumni updated successfully');
      } else {
        await api.post('/alumni', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Alumni added successfully');
      }
      
      // Reset form
      resetForm();
      
      // Force refresh by updating refreshKey
      setRefreshKey(Date.now());
      
    } catch (error) {
      console.error('Error saving alumni:', error);
      toast.error(error.response?.data?.message || 'Failed to save alumni');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (alumnus) => {
    setEditingAlumni(alumnus);
    setFormData({
      name: alumnus.name,
      role: alumnus.role,
      department_id: alumnus.department_id || '',
      graduation_year: alumnus.graduation_year || '',
      employment_status: alumnus.employment_status || '',
      employer: alumnus.employer || '',
      journey_description: alumnus.journey_description || '',
      email: alumnus.email || '',
      phone: alumnus.phone || '',
      display_order: alumnus.display_order || 0,
      photo: null
    });
    if (alumnus.photo_url) {
      setPhotoPreview(`${alumnus.photo_url}?t=${refreshKey}`);
    } else {
      setPhotoPreview(null);
    }
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this alumni?')) return;
    
    try {
      await api.delete(`/alumni/${id}`);
      toast.success('Alumni deleted successfully');
      setRefreshKey(Date.now());
    } catch (error) {
      console.error('Error deleting alumni:', error);
      toast.error('Failed to delete alumni');
    }
  };

  const handleView = (alumnus) => {
    setSelectedAlumni(alumnus);
    setShowViewModal(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingAlumni(null);
    setFormData({
      name: '',
      role: '',
      department_id: '',
      graduation_year: '',
      employment_status: '',
      employer: '',
      journey_description: '',
      email: '',
      phone: '',
      display_order: 0,
      photo: null
    });
    setPhotoPreview(null);
  };

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http')) return photoUrl;
    // Add timestamp to force refresh
    return `${photoUrl}?v=${refreshKey}`;
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'Chairman': return 'bg-yellow-100 text-yellow-800';
      case 'Vice Chairman': return 'bg-orange-100 text-orange-800';
      case 'Secretary': return 'bg-blue-100 text-blue-800';
      case 'Treasurer': return 'bg-green-100 text-green-800';
      case 'Patron': return 'bg-purple-100 text-purple-800';
      case 'Advisor': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Alumni Management</h1>
          <p className="text-gray-600 mt-1">Manage Katine TTI Alumni Association members</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ Add Alumni'}
          </Button>
        )}
      </div>

      {showForm && canManage && (
        <Card className="mb-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingAlumni ? 'Edit Alumni' : 'Add New Alumni'}
          </h2>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name *"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., John Doe"
                required
              />
              
              <div>
                <label className="label">Role *</label>
                <select
                  className="input-field"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                >
                  <option value="">Select Role</option>
                  {roles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label">Department</label>
                <select
                  className="input-field"
                  value={formData.department_id}
                  onChange={(e) => setFormData({...formData, department_id: e.target.value})}
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>

              <Input
                label="Graduation Year"
                type="number"
                value={formData.graduation_year}
                onChange={(e) => setFormData({...formData, graduation_year: e.target.value})}
                placeholder="e.g., 2024"
              />

              <Input
                label="Employment Status"
                value={formData.employment_status}
                onChange={(e) => setFormData({...formData, employment_status: e.target.value})}
                placeholder="e.g., Employed, Self-Employed, Student"
              />

              <Input
                label="Employer / Organization"
                value={formData.employer}
                onChange={(e) => setFormData({...formData, employer: e.target.value})}
                placeholder="e.g., Company Name"
              />

              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@example.com"
              />

              <Input
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="e.g., 0712345678"
              />

              <Input
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({...formData, display_order: e.target.value})}
                placeholder="Lower numbers appear first"
              />

              <div className="md:col-span-2">
                <label className="label">Journey Description</label>
                <textarea
                  className="input-field"
                  rows="6"
                  value={formData.journey_description}
                  onChange={(e) => setFormData({...formData, journey_description: e.target.value})}
                  placeholder="Write a detailed description of their journey after graduation..."
                  style={{ fontFamily: 'inherit', lineHeight: '1.6' }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="label">Photo (Max 5MB)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full p-2 border border-gray-300 rounded"
                />
                {photoPreview && (
                  <div className="mt-2">
                    <img src={photoPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : (editingAlumni ? 'Update' : 'Save')}
              </Button>
              <Button variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Alumni Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grad Year</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {alumni.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">No alumni found</td>
              </tr>
            ) : (
              alumni.map((alumnus) => (
                <tr key={alumnus.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    {alumnus.photo_url ? (
                      <img 
                        src={getImageUrl(alumnus.photo_url)} 
                        alt={alumnus.name} 
                        className="w-12 h-12 rounded-full object-cover cursor-pointer"
                        onClick={() => handleView(alumnus)}
                        onError={(e) => {
                          console.error('Image failed to load:', getImageUrl(alumnus.photo_url));
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(alumnus.name)}&background=1e3a8a&color=fff&size=100`;
                        }}
                      />
                    ) : (
                      <div 
                        className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-xl cursor-pointer"
                        onClick={() => handleView(alumnus)}
                      >
                        {alumnus.name?.charAt(0)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">{alumnus.name}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeClass(alumnus.role)}`}>
                      {alumnus.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{alumnus.department_name || '—'}</td>
                  <td className="px-6 py-4 text-gray-600">{alumnus.graduation_year || '—'}</td>
                  <td className="px-6 py-4 text-gray-600">{alumnus.display_order}</td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <button onClick={() => handleView(alumnus)} className="text-blue-600 hover:text-blue-800 mr-3">View</button>
                    <button onClick={() => handleEdit(alumnus)} className="text-primary hover:text-primary-dark mr-3">Edit</button>
                    <button onClick={() => handleDelete(alumnus.id)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Alumni Modal */}
      {showViewModal && selectedAlumni && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Alumni Profile</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
            </div>
            <div className="p-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="md:w-1/3">
                  <div className="w-full h-64 rounded-lg overflow-hidden bg-gray-100">
                    {getImageUrl(selectedAlumni.photo_url) ? (
                      <img
                        src={getImageUrl(selectedAlumni.photo_url)}
                        alt={selectedAlumni.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedAlumni.name)}&background=1e3a8a&color=fff&size=200`;
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                        <span className="text-white text-6xl font-bold">{selectedAlumni.name?.charAt(0)}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="md:w-2/3">
                  <div className="mb-4">
                    <h3 className="text-2xl font-bold text-gray-800">{selectedAlumni.name}</h3>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getRoleBadgeClass(selectedAlumni.role)}`}>
                      {selectedAlumni.role}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {selectedAlumni.department_name && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Department:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.department_name}</span>
                      </div>
                    )}
                    {selectedAlumni.graduation_year && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Graduation Year:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.graduation_year}</span>
                      </div>
                    )}
                    {selectedAlumni.employment_status && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Employment Status:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.employment_status}</span>
                      </div>
                    )}
                    {selectedAlumni.employer && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Employer:</span>
                        <span className="text-gray-900 sm:w-2/3">{selectedAlumni.employer}</span>
                      </div>
                    )}
                    {selectedAlumni.email && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Email:</span>
                        <span className="text-gray-900 sm:w-2/3">
                          <a href={`mailto:${selectedAlumni.email}`} className="text-primary hover:underline">{selectedAlumni.email}</a>
                        </span>
                      </div>
                    )}
                    {selectedAlumni.phone && (
                      <div className="flex flex-col sm:flex-row sm:justify-between py-2 border-b">
                        <span className="font-semibold text-gray-700 sm:w-1/3">Phone:</span>
                        <span className="text-gray-900 sm:w-2/3">
                          <a href={`tel:${selectedAlumni.phone}`} className="text-primary hover:underline">{selectedAlumni.phone}</a>
                        </span>
                      </div>
                    )}
                  </div>
                  {selectedAlumni.journey_description && (
                    <div className="mt-6 pt-4 border-t">
                      <h3 className="font-semibold text-gray-800 mb-3 text-lg">My Journey</h3>
                      <div className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">{selectedAlumni.journey_description}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowViewModal(false)}>Close</Button>
              {canManage && (
                <Button onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedAlumni);
                }}>Edit Profile</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAlumni;
